import React, { useState, useEffect, useCallback } from "react";
import { Link , useParams } from 'react-router-dom';
import axiosInstance from "../../../../utils/axiosInstance";
import { hasPermission } from '../../../../utils/permissionUtils';

import { FormControlLabel, Switch, Typography, Box, Alert, Radio, RadioGroup } from '@mui/material';
import ListLeads from "./ListLeads";

export default function LeadConnector() {
    const [isActive, setIsActive] = useState(false);
    const [selectedSource, setSelectedSource] = useState('customer_portal');
    const [salesforceConnected, setSalesforceConnected] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const { chatbotId: paramsChatbotId } = useParams();

     // Permission states
    const [canView, setCanView] = useState(false);
    const [canModify, setCanModify] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    const [checkingPermissions, setCheckingPermissions] = useState(true);


    // Check permissions
    useEffect(() => {
    const checkPermissions = async () => {
        setCheckingPermissions(true);
        try {
        const viewPerm = await hasPermission('campaigns', 'view');
        const modifyerm = await hasPermission('campaigns', 'add|edit');
        const deletePerm = await hasPermission('campaigns', 'delete');

        setCanView(viewPerm);
        setCanModify(modifyerm);
        setCanDelete(deletePerm);
        } catch (error) {
        console.error('Error checking permissions:', error);
        setCanView(false);
        setCanModify(false);
        setCanDelete(false);
        } finally {
        setCheckingPermissions(false);
        }
    };

    checkPermissions();
    }, []);

    const fetchSettings = useCallback(async () => {
        setIsLoading(true);
        setError('');
        
        try {
            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
            const response = await axiosInstance.get(`/api/leads/settings`, { params });
            
            if (response.data.success) {
                const data = response.data.data;
                setIsActive(data.is_active);
                setSalesforceConnected(data.salesforce_connected);
                
                // Determine which source is enabled
                if (data.enable_salesforce) {
                    setSelectedSource('salesforce');
                } else {
                    setSelectedSource('customer_portal');
                }
            }
        } catch (err) {
            console.error("Error fetching lead settings:", err);
            setError("Failed to load settings. Please try again.");
        } finally {
            setIsLoading(false);
        }
    }, [paramsChatbotId]);

    useEffect(() => {
        document.title = 'Lead Management | KlonIT';
        fetchSettings();
    }, [fetchSettings]);

    const handleSaveSettings = async () => {
        if (isActive && selectedSource === 'salesforce' && !salesforceConnected) {
            setError("Salesforce is not connected. Please connect Salesforce first.");
            return;
        }

        setIsSaving(true);
        setError('');
        setSuccess('');

        try {
            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
            await axiosInstance.post(`/api/leads/settings`, {
                is_active: isActive,
                enable_salesforce: selectedSource === 'salesforce',
                enable_customer_portal: selectedSource === 'customer_portal'
            }, { params });

            setSuccess('Settings saved successfully!');
            setRefreshTrigger(prev => prev + 1);
        } catch (err) {
            console.error("Error saving lead settings:", err);
            setError(err.response?.data?.message || "Failed to save settings.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleActiveToggle = () => {
        setIsActive(!isActive);
    };

    const handleSourceChange = (event) => {
        const newSource = event.target.value;
        
        if (newSource === 'salesforce' && !salesforceConnected) {
            setError("Please connect Salesforce first before selecting it as your lead source.");
            return;
        }
        
        setSelectedSource(newSource);
        setError('');
    };

    
    // Show loading state while checking permissions
    if (checkingPermissions) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
            <Typography>Checking permissions...</Typography>
            </Box>
        );
    }

    // Show access denied if no view permission
    if (!canView) {
        return (
            <div>
            <Alert severity="error" sx={{ mt: 6 }}>
                <strong>Access Denied</strong>
                <br />
                You don't have permission to view Lead settings. Please contact your administrator.
            </Alert>
            </div>
        );
    }
    

    return (
        <div>
            <main className="content">
                <div className="container-fluid p-0">
                    <div className="mb-3">
                        <h1 className="h3 d-inline align-middle"><strong>Lead Management</strong></h1>
                    </div>

                    {isLoading && <p>Loading configuration...</p>}

                    {error && (
                        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
                            {success}
                        </Alert>
                    )}

                    {!isLoading  && (
                        <>
                            <div className="card mb-3">
                                <div className="card-header">
                                    <h5 className="card-title mb-0">Lead Management Settings</h5>
                                </div>
                                <div className="card-body">
                                    <div className="mb-4">
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={isActive}
                                                    onChange={handleActiveToggle}
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <span style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                                                    {isActive ? 'Lead Management Active' : 'Lead Management Inactive'}
                                                </span>
                                            }
                                        />
                                        <p className="text-muted mt-2 mb-0">
                                            Enable lead management to collect and manage leads from your selected source
                                        </p>
                                    </div>

                                    {isActive && (
                                        <>
                                            <h6 className="mb-3">Select Lead Source (Choose One)</h6>
                                            <Alert severity="info" sx={{ mb: 3 }}>
                                                <strong>Important:</strong> You can only enable one lead source at a time. 
                                                All leads will be stored in the selected system.
                                            </Alert>
                                            
                                            <RadioGroup
                                                value={selectedSource}
                                                onChange={handleSourceChange}
                                            >
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    gap: 2, 
                                                    flexWrap: 'wrap',
                                                    mb: 3 
                                                }}>
                                                    {/* Customer Portal Box */}
                                                    <Box sx={{
                                                        flex: '1 1 300px',
                                                        border: '3px solid',
                                                        borderColor: selectedSource === 'customer_portal' ? '#28a745' : '#dee2e6',
                                                        borderRadius: 2,
                                                        p: 3,
                                                        backgroundColor: selectedSource === 'customer_portal' ? '#d4edda' : '#f8f9fa',
                                                        position: 'relative',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.3s ease',
                                                        '&:hover': {
                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                            transform: 'translateY(-2px)'
                                                        }
                                                    }}
                                                    onClick={() => handleSourceChange({ target: { value: 'customer_portal' } })}
                                                    >
                                                        <div style={{ 
                                                            position: 'absolute',
                                                            top: 10,
                                                            right: 10
                                                        }}>
                                                            <span style={{
                                                                backgroundColor: '#28a745',
                                                                color: 'white',
                                                                padding: '4px 8px',
                                                                borderRadius: '4px',
                                                                fontSize: '0.75rem',
                                                                fontWeight: 'bold'
                                                            }}>
                                                                CONNECTED
                                                            </span>
                                                        </div>
                                                        <FormControlLabel
                                                            value="customer_portal"
                                                            control={
                                                                <Radio 
                                                                    sx={{
                                                                        '&.Mui-checked': {
                                                                            color: '#28a745'
                                                                        }
                                                                    }}
                                                                />
                                                            }
                                                            label={
                                                                <div>
                                                                    <h5 className="mb-2">
                                                                        <i className="fa fa-user-circle me-2" style={{ color: '#28a745' }}></i>
                                                                        Customer Portal
                                                                    </h5>
                                                                    <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                                                                        Collect leads directly from your website chatbot interactions. Always available.
                                                                    </p>
                                                                </div>
                                                            }
                                                            sx={{ width: '100%', m: 0 }}
                                                        />
                                                    </Box>

                                                    {/* Salesforce Box */}
                                                    <Box sx={{
                                                        flex: '1 1 300px',
                                                        border: '3px solid',
                                                        borderColor: selectedSource === 'salesforce' ? '#0d6efd' : '#dee2e6',
                                                        borderRadius: 2,
                                                        p: 3,
                                                        backgroundColor: selectedSource === 'salesforce' ? '#cfe2ff' : '#f8f9fa',
                                                        position: 'relative',
                                                        opacity: salesforceConnected ? 1 : 0.6,
                                                        cursor: salesforceConnected ? 'pointer' : 'not-allowed',
                                                        transition: 'all 0.3s ease',
                                                        '&:hover': salesforceConnected ? {
                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                            transform: 'translateY(-2px)'
                                                        } : {}
                                                    }}
                                                    onClick={() => salesforceConnected && handleSourceChange({ target: { value: 'salesforce' } })}
                                                    >
                                                        <div style={{ 
                                                            position: 'absolute',
                                                            top: 10,
                                                            right: 10
                                                        }}>
                                                            <span style={{
                                                                backgroundColor: salesforceConnected ? '#28a745' : '#6c757d',
                                                                color: 'white',
                                                                padding: '4px 8px',
                                                                borderRadius: '4px',
                                                                fontSize: '0.75rem',
                                                                fontWeight: 'bold'
                                                            }}>
                                                                {salesforceConnected ? 'CONNECTED' : 'NOT CONNECTED'}
                                                            </span>
                                                        </div>
                                                        <FormControlLabel
                                                            value="salesforce"
                                                            control={
                                                                <Radio 
                                                                    disabled={!salesforceConnected}
                                                                    sx={{
                                                                        '&.Mui-checked': {
                                                                            color: '#0d6efd'
                                                                        }
                                                                    }}
                                                                />
                                                            }
                                                            label={
                                                                <div>
                                                                    <h5 className="mb-2">
                                                                        <i className="fa fa-cloud me-2" style={{ color: '#0d6efd' }}></i>
                                                                        Salesforce
                                                                    </h5>
                                                                    <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
                                                                        Store leads directly in your Salesforce CRM system.
                                                                    </p>
                                                                    {!salesforceConnected && canModify && (
                                                                        <Link
                                                                            to={`/chatbot/${paramsChatbotId}/connectors/salesforce`}
                                                                            className="btn btn-sm btn-outline-primary"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            >
                                                                            <i className="fa fa-link me-1"></i>
                                                                            Connect Salesforce
                                                                        </Link>
                                                                    )}
                                                                </div>
                                                            }
                                                            sx={{ width: '100%', m: 0 }}
                                                        />
                                                    </Box>
                                                </Box>
                                            </RadioGroup>

                                            
                                        </>
                                    )}

                                    { canModify && (

                                    <div className="d-flex justify-content-end">
                                        <button 
                                            className="btn btn-primary btn-lg"
                                            onClick={handleSaveSettings}
                                            disabled={isSaving}
                                        >
                                            {isSaving ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Saving...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fa fa-save me-2"></i>
                                                    Save Settings
                                                </>
                                            )}
                                        </button>
                                    </div>
                                    )}
                                </div>
                            </div>

                            {isActive && (
                                <div className="card">
                                    <div className="card-header">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <h5 className="card-title mb-0">Leads</h5>
                                                <p className="mb-0">
                                                    Managing leads from: {' '}
                                                    {selectedSource === 'salesforce' ? (
                                                        <span className="badge bg-primary">
                                                            <i className="fa fa-cloud me-1"></i>Salesforce
                                                        </span>
                                                    ) : (
                                                        <span className="badge bg-success">
                                                            <i className="fa fa-user-circle me-1"></i>Customer Portal
                                                        </span>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <ListLeads 
                                            refreshTrigger={refreshTrigger} 
                                            chatbotId={paramsChatbotId}
                                            activeSource={selectedSource}
                                            permission={{'canModify': canModify, 'canDelete':canDelete}}
                                        />
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </main>
        </div>
    );
}